import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "./Admin";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowUpRight, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

type Row = {
  id: string;
  addon_id: string;
  booking_id: string | null;
  delta: number;
  reason: string;
  actor_id: string | null;
  metadata: any;
  created_at: string;
};

const fmtDate = (s: string) => new Date(s).toLocaleString();

const AdminAddonStockAudit = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [addons, setAddons] = useState<{ id: string; name: string; stock: number | null }[]>([]);
  const [filterAddon, setFilterAddon] = useState<string>("all");
  const [filterBooking, setFilterBooking] = useState<string>("");
  const [filterRefund, setFilterRefund] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("addons").select("id,name,stock").order("name").then(({ data }) => setAddons(data ?? []));
  }, []);

  useEffect(() => {
    setLoading(true);
    let q = supabase
      .from("addon_stock_audit")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);
    if (filterAddon !== "all") q = q.eq("addon_id", filterAddon);
    if (filterBooking.trim()) q = q.eq("booking_id", filterBooking.trim());
    if (dateFrom) q = q.gte("created_at", new Date(dateFrom).toISOString());
    if (dateTo) {
      const end = new Date(dateTo); end.setHours(23, 59, 59, 999);
      q = q.lte("created_at", end.toISOString());
    }
    q.then(({ data }) => {
      let result = (data ?? []) as Row[];
      if (filterRefund.trim()) {
        const needle = filterRefund.trim();
        result = result.filter((r) => {
          const m = r.metadata ?? {};
          return (
            m.refund_id === needle ||
            m.stripe_refund_id === needle ||
            JSON.stringify(m).includes(needle)
          );
        });
      }
      setRows(result);
      setLoading(false);
    });
  }, [filterAddon, filterBooking, filterRefund, dateFrom, dateTo]);

  const addonMap = useMemo(() => {
    const m = new Map<string, { name: string; stock: number | null }>();
    addons.forEach((a) => m.set(a.id, { name: a.name, stock: a.stock }));
    return m;
  }, [addons]);

  const exportCsv = () => {
    const headers = ["created_at", "addon_id", "addon_name", "delta", "reason", "booking_id", "actor_id", "metadata"];
    const escape = (v: any) => {
      const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push([
        r.created_at,
        r.addon_id,
        addonMap.get(r.addon_id)?.name ?? "",
        r.delta,
        r.reason,
        r.booking_id ?? "",
        r.actor_id ?? "",
        r.metadata,
      ].map(escape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `addon-stock-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setFilterAddon("all"); setFilterBooking(""); setFilterRefund(""); setDateFrom(""); setDateTo("");
  };

  return (
    <AdminLayout title="ADD-ON STOCK AUDIT">
      <div className="mb-6 space-y-4">
        <p className="text-muted-foreground font-body text-sm">
          Every increment / decrement tied to a booking or refund. Showing latest {rows.length}.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Add-on</Label>
            <Select value={filterAddon} onValueChange={setFilterAddon}>
              <SelectTrigger className="font-body"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All add-ons</SelectItem>
                {addons.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}{a.stock != null ? ` · stock ${a.stock}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Booking ID</Label>
            <Input placeholder="uuid" value={filterBooking} onChange={(e) => setFilterBooking(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Refund ID</Label>
            <Input placeholder="re_…" value={filterRefund} onChange={(e) => setFilterRefund(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button onClick={exportCsv} disabled={rows.length === 0} className="font-body flex-1">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" onClick={resetFilters} className="font-body">Reset</Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground">No stock movements yet.</p>
      ) : (
        <div className="bg-panel border border-border overflow-x-auto">
          <table className="w-full text-sm font-body">
            <thead className="text-xs font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">Add-on</th>
                <th className="text-right p-3">Δ</th>
                <th className="text-left p-3">Reason</th>
                <th className="text-left p-3">Booking</th>
                <th className="text-left p-3">Actor</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = addonMap.get(r.addon_id);
                const positive = r.delta > 0;
                return (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="p-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="p-3">{meta?.name ?? r.addon_id.slice(0, 8)}</td>
                    <td className={`p-3 text-right font-mono ${positive ? "text-emerald-500" : "text-destructive"}`}>
                      {positive ? "+" : ""}{r.delta}
                    </td>
                    <td className="p-3 font-mono text-xs">{r.reason}</td>
                    <td className="p-3">
                      {r.booking_id ? (
                        <Link to={`/admin/bookings/${r.booking_id}`} className="text-primary inline-flex items-center gap-1 font-mono text-xs">
                          {r.booking_id.slice(0, 8)} <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{r.actor_id ? r.actor_id.slice(0, 8) : "system"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAddonStockAudit;